<template>
  <HrPageLayout
    title="Smart Attendance System"
    subtitle="Manage and track employee attendance"
    icon="⏰"
    :breadcrumb-items="[
      { label: 'Leave & Attendance' },
      { label: 'Smart Attendance' }
    ]"
  >
    <template #header-actions>
      <button @click="downloadAttendanceReport()" type="button" class="hr-btn hr-btn-primary">
        <i class="bx bx-download"></i> {{ downloadText }}
      </button>
      <div class="process-dropdown-wrap">
        <button class="hr-btn hr-btn-success" type="button" @click="showProcessDropdown = !showProcessDropdown">
          <i class="bx bx-refresh"></i> {{ processText }}
        </button>
        <div v-if="showProcessDropdown" class="process-dropdown">
          <h6>Process Attendance Data</h6>
          
          <!-- Single Date Processing -->
          <div class="process-section">
            <p class="section-label">Process Single Date</p>
            <label class="hr-form-label">Date:</label>
            <input v-model="processDate" type="date" class="hr-input hr-mb-2">
            <button @click="processAttendanceData(); showProcessDropdown = false" type="button" class="hr-btn hr-btn-success hr-mb-3" style="width: 100%;">
              <i class="bx bx-refresh hr-me-2"></i> Process Selected Date
            </button>
            <button @click="processAttendanceDataToday(); showProcessDropdown = false" type="button" class="hr-btn hr-btn-secondary hr-mb-3" style="width: 100%;">
              <i class="bx bx-calendar hr-me-2"></i> Process Today Only
            </button>
          </div>
          
          <!-- Date Range Processing -->
          <div class="process-section" style="border-top: 1px solid #e2e8f0; padding-top: 12px;">
            <p class="section-label">Process Date Range</p>
            <label class="hr-form-label">Start Date:</label>
            <input v-model="processStartDate" type="date" class="hr-input hr-mb-2">
            <label class="hr-form-label">End Date:</label>
            <input v-model="processEndDate" type="date" class="hr-input hr-mb-2">
            <button @click="processAttendanceDateRange(); showProcessDropdown = false" type="button" class="hr-btn hr-btn-primary" style="width: 100%;">
              <i class="bx bx-calendar-range hr-me-2"></i> Process Date Range
            </button>
          </div>
        </div>
      </div>
      <!-- Auto-process status indicator -->
      <div class="auto-process-indicator" :class="{ active: autoProcessing }">
        <i class="bx" :class="autoProcessing ? 'bx-loader-alt bx-spin' : 'bx-time-five'"></i>
        <span v-if="autoProcessing">Auto-processing...</span>
        <span v-else-if="lastAutoProcessed">Auto: {{ lastAutoProcessed }}</span>
        <span v-else>Auto: every 5 min</span>
      </div>
    </template>

    <div class="attendance-timesheet-content">
      <!-- Dashboard Cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-user-check"></i></div>
          <p class="hr-metric-label">Present Today</p>
          <h2 class="hr-metric-value">{{ dashboardMetrics.todayMetrics?.present_count || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <p class="hr-metric-label">Late Arrivals</p>
          <h2 class="hr-metric-value">{{ dashboardMetrics.todayMetrics?.late_count || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-time"></i></div>
          <p class="hr-metric-label">Overtime Today</p>
          <h2 class="hr-metric-value">{{ dashboardMetrics.todayMetrics?.overtime_count || 0 }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-trending-up"></i></div>
          <p class="hr-metric-label">Avg Working Hours</p>
          <h2 class="hr-metric-value">{{ formatHours(dashboardMetrics.todayMetrics?.avg_working_hours) || '0.00' }}</h2>
        </div>
      </div>

      <!-- Analytics Cards -->
      <div class="hr-content-grid hr-mb-4" v-if="analytics">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-error-circle"></i> Frequent Late Comers</h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead><tr><th>Employee</th><th>Late Days</th><th>Avg Minutes</th></tr></thead>
                <tbody>
                  <tr v-for="(emp, index) in analytics.frequentLateComers" :key="index">
                    <td>{{ emp.employee_name }}</td>
                    <td><span class="hr-badge hr-badge-warning">{{ emp.late_days }}</span></td>
                    <td>{{ Math.round(emp.avg_late_minutes) }} min</td>
                  </tr>
                  <tr v-if="!analytics.frequentLateComers?.length">
                    <td colspan="3" class="hr-text-muted" style="text-align: center;">No late arrivals in selected period</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-time"></i> Overtime Champions</h5>
          </div>
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead><tr><th>Employee</th><th>Total OT Hours</th><th>OT Days</th></tr></thead>
                <tbody>
                  <tr v-for="(emp, index) in analytics.overtimeChampions" :key="index">
                    <td>{{ emp.employee_name }}</td>
                    <td><span class="hr-badge hr-badge-primary">{{ formatHours(emp.total_overtime) }}</span></td>
                    <td>{{ emp.overtime_days }}</td>
                  </tr>
                  <tr v-if="!analytics.overtimeChampions?.length">
                    <td colspan="3" class="hr-text-muted" style="text-align: center;">No overtime recorded in selected period</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Attendance Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-calendar-check"></i> Attendance Records</h5>
          <div class="hr-filters-row">
            <div class="hr-search-wrap" style="min-width: 200px;">
              <input v-model="search" type="text" class="hr-input" placeholder="Search employee...">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <select class="hr-select" style="width: 180px;" v-model="departmentName">
              <option value="">All Departments</option>
              <option v-for="(item, index) in departments" :value="item.departmentName" :key="index">{{ item.departmentName }}</option>
            </select>
            <select class="hr-select" style="width: 150px;" v-model="statusFilter">
              <option value="">All Status</option>
              <option value="Present">Present</option>
              <option value="Late">Late</option>
              <option value="Early_Departure">Early Departure</option>
              <option value="Overtime">Overtime</option>
              <option value="Absent">Absent</option>
              <option value="Partial">Partial</option>
            </select>
            <input v-model="startDate" class="hr-input" type="date" style="width: 140px;">
            <input v-model="endDate" class="hr-input" type="date" style="width: 140px;">
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading attendance data...</p>
          </div>

          <div v-else-if="processing" class="hr-processing-banner">
            <div class="hr-spinner small"></div>
            <span>Processing attendance data for {{ processDate }}...</span>
          </div>

          <template v-else>
            <div class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Shift</th>
                    <th>In/Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Breaks</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                        <tbody>
                        <tr v-for="(item, index) in attendance" :key="index">
                          <td>
                            <div>
                              <strong>{{item.employee_name}}</strong><br>
                              <small class="text-muted">{{item.employee_id}}</small><br>
                              <small class="text-muted">{{item.departmentName || 'N/A'}}</small>
                            </div>
                          </td>
                          <td>{{formatDate(item.date)}}</td>
                          <td>
                            <div v-if="item.shift_start">
                              <small class="text-muted">{{item.shift_start}} - {{item.shift_end}}</small><br>
                              <small class="hr-badge hr-badge-muted">{{ item.shift_name || 'Regular' }}</small>
                            </div>
                            <div v-else>
                              <small class="text-muted">No shift assigned</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <strong class="text-success">In:</strong> {{item.first_in || 'N/A'}}<br>
                              <strong class="text-danger">Out:</strong> {{item.last_out || 'N/A'}}
                            </div>
                          </td>
                          <td>
                            <div>
                              <strong>{{item.total_hours}}h</strong><br>
                              <small class="text-muted" v-if="parseFloat(item.overtime_hours) > 0">
                                OT: {{item.overtime_hours}}h
                              </small>
                              <small class="text-muted" v-if="parseFloat(item.break_hours) > 0">
                                Break: {{item.break_hours}}h
                              </small>
                            </div>
                          </td>
                          <td>
                                <span class="hr-badge" :class="'hr-badge-' + (item.status_color || 'muted')">
                                  {{ formatStatus(item.status) }}
                                </span>
                            <div v-if="item.late_minutes > 0" class="mt-1">
                              <small class="text-warning">Late: {{item.late_minutes}}min</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span class="hr-badge hr-badge-muted">{{ item.total_entries }}/{{ item.total_exits }}</span>
                              <div v-if="item.longest_break > 0" class="mt-1">
                                <small class="text-muted">Longest: {{item.longest_break_formatted}}</small>
                              </div>
                            </div>
                          </td>
                          <td>{{item.location || 'N/A'}}</td>
                          <td>
                            <button @click="viewEmployeeBreaks(item.employee_id, item.date)" class="hr-btn hr-btn-info hr-btn-sm hr-btn-icon" title="View Breaks">
                              <i class="bx bx-time-five"></i>
                            </button>
                            <button @click="viewRawTimesheet(item.employee_id, item.date)" class="hr-btn hr-btn-secondary hr-btn-sm hr-btn-icon" title="Raw Data">
                              <i class="bx bx-data"></i>
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>

            <div v-if="!attendance.length && !loading" class="hr-empty-state">
              <i class="bx bx-calendar-x"></i>
              <p>No attendance records found for the selected criteria</p>
            </div>
            </div>

            <div v-if="totalPages > 1" class="hr-pagination">
              <div class="hr-pagination-info">
                Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} entries
              </div>
              <nav class="hr-pagination-nav">
                <button class="hr-page-btn" :disabled="currentPage === 1" @click.prevent="prevPage()">Previous</button>
                <template v-for="page in visiblePages" :key="page">
                  <button class="hr-page-btn" :class="{ active: currentPage === page }" @click.prevent="goToPage(page)">{{ page }}</button>
                </template>
                <button class="hr-page-btn" :disabled="currentPage === totalPages" @click.prevent="nextPage()">Next</button>
              </nav>
            </div>
          </template>
        </div>
      </div>

      <!-- Employee Breaks Modal -->
      <ModalWrapper ref="breaksModal" title="Employee Break Details" size="large">
        <template #body>
          <div class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Break Out</th>
                  <th>Break In</th>
                  <th>Duration</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(brk, index) in employeeBreaks" :key="index">
                  <td>{{ formatDate(brk.date) }}</td>
                  <td>{{ brk.break_out }}</td>
                  <td>{{ brk.break_in || 'Still Out' }}</td>
                  <td>{{ brk.break_duration_formatted }}</td>
                  <td>{{ brk.location_out }} → {{ brk.location_in || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-secondary" @click="$refs.breaksModal.close()">Close</button>
        </template>
      </ModalWrapper>

      <!-- Raw Timesheet Modal -->
      <ModalWrapper ref="rawTimesheetModal" title="Raw Timesheet Data" size="large">
        <template #body>
          <div class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Employee</th>
                  <th>Direction</th>
                  <th>Device/Location</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(entry, index) in rawTimesheet" :key="index">
                  <td>{{ entry.acct }}</td>
                  <td>{{ entry.pname }}</td>
                  <td>
                    <span class="hr-badge" :class="entry.direction === 'enter' ? 'hr-badge-success' : 'hr-badge-danger'">{{ entry.direction }}</span>
                  </td>
                  <td>{{ entry.dname }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <template #footer>
          <button class="hr-btn hr-btn-secondary" @click="$refs.rawTimesheetModal.close()">Close</button>
        </template>
      </ModalWrapper>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import moment from "moment/moment";
import { authUrl, execute, executeDownload, getRight } from "@/api";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "SmartAttendance",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      processing: false,
      downloadText: 'Download Report',
      processText: 'Process Data',

      // Pagination
      currentPage: 1,
      totalPages: 0,
      pageSize: 25,
      totalCount: 0,

      // Filters - CHANGED: Both startDate and endDate default to today (0 days from today)
      search: "",
      departmentName: '',
      statusFilter: '',
      startDate: this.getDateString(0), // Changed from -7 to 0 (today)
      endDate: this.getDateString(0), // Already was today

      // Process Data specific date
      processDate: this.getDateString(0), // today by default

      // Data
      attendance: [],
      departments: [],
      dashboardMetrics: {},
      analytics: {},
      employeeBreaks: [],
      rawTimesheet: [],

      message: null,
      searchTimeout: null,
      showProcessDropdown: false,

      // Date range processing
      processStartDate: this.getDateString(0),
      processEndDate: this.getDateString(0),

      // Auto-processing every 5 minutes
      autoProcessInterval: null,
      autoProcessing: false,
      lastAutoProcessed: null
    };
  },

  mounted() {
    this.initializeData();
    this.startAutoProcess();
  },

  beforeUnmount() {
    if (this.autoProcessInterval) {
      clearInterval(this.autoProcessInterval);
      this.autoProcessInterval = null;
    }
  },

  computed: {
    visiblePages() {
      return this.pagination(this.currentPage, this.totalPages, 5);
    }
  },

  watch: {
    startDate(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.currentPage = 1;
        this.getSmartAttendance();
      }
    },
    endDate(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.currentPage = 1;
        this.getSmartAttendance();
      }
    },
    search(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.currentPage = 1;
        this.debounceSearch();
      }
    },
    departmentName(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.currentPage = 1;
        this.getSmartAttendance();
      }
    },
    statusFilter(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.currentPage = 1;
        this.getSmartAttendance();
      }
    }
  },

  methods: {
    async initializeData() {
      await Promise.all([
        this.getDepartments(),
        this.getDashboardMetrics(),
        this.getSmartAttendance()
      ]);
    },

    startAutoProcess() {
      // Run immediately on mount, then every 5 minutes
      this.autoProcessToday();
      this.autoProcessInterval = setInterval(() => {
        this.autoProcessToday();
      }, 5 * 60 * 1000); // 5 minutes
    },

    async autoProcessToday() {
      // Skip if a manual process or another auto-process is already running
      if (this.processing || this.autoProcessing) return;

      this.autoProcessing = true;
      const today = this.getDateString(0);

      const data = new FormData();
      data.append("function", "autoProcessAttendance");
      data.append("date", today);
      data.append("incremental", "1");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          const skipped = res.data.data && res.data.data.skippedNoNew;
          this.lastAutoProcessed = skipped
            ? `${new Date().toLocaleTimeString()} (no new punches)`
            : new Date().toLocaleTimeString();
          // Refresh the view if today is within the current date range
          if (today >= this.startDate && today <= this.endDate) {
            await this.getSmartAttendance();
          }
          await this.getDashboardMetrics();
        }
      } catch (e) {
        console.error('Auto-process error:', e);
      } finally {
        this.autoProcessing = false;
      }
    },

    getDateString(daysFromToday) {
      const date = new Date();
      date.setDate(date.getDate() + daysFromToday);
      return date.toISOString().split('T')[0];
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getSmartAttendance();
      }, 500);
    },

    async getSmartAttendance() {
      this.loading = true;
      this.attendance = [];

      const data = new FormData();
      data.append("function", "getSmartAttendance");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("startDate", this.startDate);
      data.append("endDate", this.endDate);
      data.append("search", this.search);
      data.append("departmentName", this.departmentName);
      data.append("status", this.statusFilter);

      try {
        const res = await execute(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          this.attendance = res.data.data.attendance;
          this.totalCount = res.data.data.totalCount;
          this.analytics = res.data.data.analytics;
          this.totalPages = Math.ceil(this.totalCount / this.pageSize);
        } else {
          this.message = res.data.message;
        }
      } catch (e) {
        this.loading = false;
        this.message = e.message;
      }
    },

    async getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.departments = res.data.data.departments;
        }
      } catch (e) {
        console.error('Error loading departments:', e);
      }
    },

    async getDashboardMetrics() {
      const data = new FormData();
      data.append("function", "getAttendanceDashboard");
      data.append("date", this.getDateString(0)); // today

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.dashboardMetrics = res.data.data;
        }
      } catch (e) {
        console.error('Error loading dashboard metrics:', e);
      }
    },

    async processAttendanceData() {
      if (!this.processDate) {
        this.showNotification('Please select a date to process', 'error');
        return;
      }

      this.processing = true;
      this.processText = 'Processing...';

      const data = new FormData();
      data.append("function", "processTimesheetData");
      data.append("date", this.processDate);

      try {
        const res = await execute(data, authUrl);
        this.processing = false;
        this.processText = 'Process Data';

        if (res.data.success) {
          this.showNotification(`Attendance data processed successfully for ${this.formatDate(this.processDate)}!`, 'success');

          this.showProcessDropdown = false;

          // Refresh data if the processed date is within the current view range
          if (this.processDate >= this.startDate && this.processDate <= this.endDate) {
            await this.getSmartAttendance();
          }

          await this.getDashboardMetrics();
        } else {
          this.showNotification(res.data.message || 'Error processing attendance data', 'error');
        }
      } catch (e) {
        this.processing = false;
        this.processText = 'Process Data';
        this.showNotification('Error processing attendance data: ' + e.message, 'error');
        console.error('Processing error:', e);
      }
    },

    async processAttendanceDataToday() {
      this.processDate = this.getDateString(0); // Set to today
      await this.processAttendanceData();
    },
    async processAttendanceDateRange() {
      if (!this.processStartDate || !this.processEndDate) {
        this.showNotification('Please select both start and end dates', 'error');
        return;
      }

      if (this.processStartDate > this.processEndDate) {
        this.showNotification('Start date must be before or equal to end date', 'error');
        return;
      }

      this.processing = true;
      this.processText = 'Processing...';

      // Calculate total days to process
      const startDate = new Date(this.processStartDate);
      const endDate = new Date(this.processEndDate);
      const timeDifference = endDate.getTime() - startDate.getTime();
      const totalDays = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1;

      try {
        let currentDate = new Date(this.processStartDate);
        let processedDays = 0;
        let failedDays = [];

        // Process each day in the date range
        while (currentDate <= endDate) {
          const dateString = currentDate.toISOString().split('T')[0];
          
          const data = new FormData();
          data.append("function", "processTimesheetData");
          data.append("date", dateString);

          try {
            const res = await execute(data, authUrl);
            if (res.data.success) {
              processedDays++;
            } else {
              failedDays.push(dateString);
            }
          } catch (e) {
            failedDays.push(dateString);
          }

          // Move to next day
          currentDate.setDate(currentDate.getDate() + 1);
        }

        this.processing = false;
        this.processText = 'Process Data';

        // Show result message
        if (processedDays > 0) {
          let message = `Attendance data processed successfully for ${processedDays}/${totalDays} days (${this.formatDate(this.processStartDate)} to ${this.formatDate(this.processEndDate)})!`;
          if (failedDays.length > 0) {
            message += ` (${failedDays.length} days failed)`;
          }
          this.showNotification(message, processedDays === totalDays ? 'success' : 'info');
        } else {
          this.showNotification('No days were processed. Please check your date range.', 'error');
          return;
        }

        this.showProcessDropdown = false;

        // Refresh data if the processed date range overlaps with the current view range
        if (this.processEndDate >= this.startDate && this.processStartDate <= this.endDate) {
          await this.getSmartAttendance();
        }

        await this.getDashboardMetrics();
      } catch (e) {
        this.processing = false;
        this.processText = 'Process Data';
        this.showNotification('Error processing attendance data: ' + e.message, 'error');
        console.error('Processing error:', e);
      }
    },

    async viewEmployeeBreaks(employeeId, date) {
      const data = new FormData();
      data.append("function", "getEmployeeBreaks");
      data.append("employee_id", employeeId);
      data.append("startDate", date);
      data.append("endDate", date);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.employeeBreaks = res.data.data.breaks;
          this.$nextTick(() => this.$refs.breaksModal?.open());
        }
      } catch (e) {
        this.showNotification('Error loading break details', 'error');
      }
    },

    async viewRawTimesheet(employeeId, date) {
      // Get raw timesheet data for the employee on that date
      const data = new FormData();
      data.append("function", "getRawTimesheet");
      data.append("employee_id", employeeId);
      data.append("date", date);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.rawTimesheet = res.data.data.timesheet;
          this.$nextTick(() => this.$refs.rawTimesheetModal?.open());
        }
      } catch (e) {
        this.showNotification('Error loading raw timesheet data', 'error');
      }
    },

    async downloadAttendanceReport() {
      this.downloadText = "Generating...";

      const data = new FormData();
      data.append("function", "downloadAttendanceReport");
      data.append("startDate", this.startDate);
      data.append("endDate", this.endDate);
      data.append("search", this.search);
      data.append("departmentName", this.departmentName);
      data.append("status", this.statusFilter);

      try {
        const response = await executeDownload(data, authUrl);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(response.data);
        link.download = `smart_attendance_${this.startDate}_to_${this.endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.downloadText = "Download Report";
      } catch (e) {
        this.downloadText = "Download Report";
        this.showNotification('Error downloading report', 'error');
      }
    },

    // Notification method that works with or without toast library
    showNotification(message, type = 'info') {
      // Try to use toast if available
      if (this.$toast) {
        if (type === 'success') {
          this.$toast.success(message);
        } else if (type === 'error') {
          this.$toast.error(message);
        } else {
          this.$toast.info(message);
        }
        return;
      }

      // Fallback to browser alert or console
      if (type === 'error') {
        console.error('Error:', message);
        alert('Error: ' + message);
      } else if (type === 'success') {
        console.log('Success:', message);
        // You could create a temporary success message div here
        this.showTemporaryMessage(message, 'success');
      } else {
        console.info('Info:', message);
        this.showTemporaryMessage(message, 'info');
      }
    },

    // Alternative notification method using Bootstrap alerts
    showTemporaryMessage(message, type = 'info') {
      // Create a temporary alert div
      const alertDiv = document.createElement('div');
      alertDiv.className = `alert alert-${type === 'success' ? 'success' : type === 'error' ? 'danger' : 'info'} alert-dismissible fade show position-fixed`;
      alertDiv.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alertDiv.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(alertDiv);

      // Auto remove after 5 seconds
      setTimeout(() => {
        if (alertDiv.parentNode) {
          alertDiv.parentNode.removeChild(alertDiv);
        }
      }, 5000);
    },

    // Pagination methods
    pagination(currentPage, totalPages, maxVisibleButtons) {
      let pages = [];
      let start = currentPage - Math.floor(maxVisibleButtons / 2);
      let end = currentPage + Math.floor(maxVisibleButtons / 2);

      if (start < 1) {
        start = 1;
        end = start + maxVisibleButtons - 1;
        if (end > totalPages) {
          end = totalPages;
        }
      }

      if (end > totalPages) {
        end = totalPages;
        start = end - maxVisibleButtons + 1;
        if (start < 1) {
          start = 1;
        }
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getSmartAttendance();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getSmartAttendance();
      }
    },

    goToPage(page) {
      if (page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getSmartAttendance();
      }
    },

    // Formatting methods
    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    },

    formatTime(time) {
      return moment(time, 'HH:mm:ss').format('HH:mm');
    },

    formatHours(hours) {
      return parseFloat(hours).toFixed(2);
    },

    formatStatus(status) {
      return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num);
    },

    getRight(type) {
      return getRight(type);
    }
  }
};
</script>

<style scoped>
.process-dropdown-wrap { position: relative; z-index: 10; }
.process-dropdown {
  position: absolute; top: 100%; right: 0; margin-top: 8px;
  padding: 16px; background: white; border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15); border: 1px solid #e2e8f0;
  min-width: 260px; z-index: 1050;
}
.process-dropdown h6 { margin: 0 0 12px 0; font-size: 14px; }

.auto-process-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  white-space: nowrap;
}
.auto-process-indicator.active {
  color: #059669;
  background: #ecfdf5;
  border-color: #6ee7b7;
}
.hr-filters-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.hr-processing-banner {
  display: flex; align-items: center; gap: 12px; padding: 16px;
  background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; color: #1e40af;
}
.hr-processing-banner .hr-spinner.small { width: 24px; height: 24px; border-width: 2px; }

.mini-stats-wid {
  border: none;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
}

.mini-stats-wid:hover {
  transform: translateY(-2px);
}

.table-responsive {
  max-height: none;
}

.badge {
  font-size: 0.75em;
  padding: 0.5em 0.75em;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.search-box .search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #8492a6;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}

.pagination .page-link {
  padding: 0.5rem 0.75rem;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0;
}

.text-muted {
  color: #6c757d !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.dropdown-menu {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.dropdown-header {
  font-weight: 600;
  color: #495057;
}

.alert {
  border-radius: 0.375rem;
}
</style>